// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Grips on a selected wall (frontend-u0o.3; design.md §4, Revit and
 * Spacio): its two endpoints, the move grip at its middle (perpendicular to
 * its axis) and the face grip (thickness), what each drag does to the
 * model, and the temporary dimensions around it (its length, and its
 * distance to the nearest parallel wall on either side — click one to type
 * a value). Every drag ends as one batch of `wall.update` ops, the walls it
 * drags along included:
 *
 *  - an endpoint takes the walls joined at it along (the corner kept),
 *    unless the caller says not to (Ctrl);
 *  - a move takes every wall that ends on the moved wall — at its ends (a
 *    corner) or along it (a tee) — along its own direction to the moved
 *    axis, so corners and tees stay closed;
 *  - the face grip sets the thickness about the axis.
 *
 * World x/y. Pure: no React, no DOM.
 */

import type { ModelEditOp, SolidWall } from '../api/types';
import type { GeometriModel } from './geometri';
import { lineIntersection, nearestOnSegment } from './inference';
import type { XY } from './planWalls';

export type WallEnd = 'start' | 'end';

export type GripKind = 'start' | 'end' | 'move' | 'thickness';

export interface Grip {
  kind: GripKind;
  /** Where it is drawn (world x/y). */
  at: XY;
  /** The move grip's two arrows point along this (the wall's normal). */
  normal?: XY;
}

const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const dot = (a: XY, b: XY) => a[0] * b[0] + a[1] * b[1];
const dist = (a: XY, b: XY) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const r6 = (v: number) => Math.round(v * 1e6) / 1e6;
const pt = (p: XY): [number, number] => [r6(p[0]), r6(p[1])];

/** Unit direction and left normal of a wall's axis; null for a wall of no length. */
export function frame(w: Pick<SolidWall, 'start' | 'end'>): { dir: XY; normal: XY; length: number } | null {
  const d = sub(w.end, w.start);
  const L = Math.hypot(d[0], d[1]);
  if (!(L > 1e-9)) return null;
  const dir: XY = [d[0] / L, d[1] / L];
  return { dir, normal: [-dir[1], dir[0]], length: L };
}

export function wallGrips(w: SolidWall): Grip[] {
  const f = frame(w);
  if (!f) return [];
  const mid = mul(add(w.start, w.end), 0.5);
  const half = (w.thickness ?? 0) / 2;
  return [
    { kind: 'start', at: w.start },
    { kind: 'end', at: w.end },
    { kind: 'move', at: mid, normal: f.normal },
    { kind: 'thickness', at: add(mid, mul(f.normal, half)), normal: f.normal },
  ];
}

/** Two ends this close (metres, beyond half the thicker wall) count as joined. */
const JOIN_SLACK = 0.02;

/** The walls (other than `w`) on its storey with an end at `w`'s `end`. */
export function joinedAt(walls: readonly SolidWall[], w: SolidWall, end: WallEnd): { wall: SolidWall; end: WallEnd }[] {
  const p = w[end];
  const out: { wall: SolidWall; end: WallEnd }[] = [];
  for (const o of walls) {
    if (o.guid === w.guid || o.storey_id !== w.storey_id) continue;
    const reach = Math.max(w.thickness ?? 0, o.thickness ?? 0) / 2 + JOIN_SLACK;
    for (const e of ['start', 'end'] as const) if (dist(o[e], p) <= reach) out.push({ wall: o, end: e });
  }
  return out;
}

/** The walls with an end on `w` — at its ends or along its body (a tee). */
export function endingOn(walls: readonly SolidWall[], w: SolidWall): { wall: SolidWall; end: WallEnd }[] {
  const out: { wall: SolidWall; end: WallEnd }[] = [];
  const half = (w.thickness ?? 0) / 2;
  for (const o of walls) {
    if (o.guid === w.guid || o.storey_id !== w.storey_id) continue;
    for (const e of ['start', 'end'] as const) {
      const { point } = nearestOnSegment(w.start, w.end, o[e]);
      if (dist(point, o[e]) <= Math.max(half, (o.thickness ?? 0) / 2) + JOIN_SLACK) out.push({ wall: o, end: e });
    }
  }
  return out;
}

function wallsOf(model: GeometriModel): readonly SolidWall[] {
  return model.walls ?? [];
}

/** The ops of a drag, keyed by wall, merged into one `wall.update` each. */
class Batch {
  private sets = new Map<string, Record<string, unknown>>();
  set(guid: string, patch: Record<string, unknown>): void {
    this.sets.set(guid, { ...(this.sets.get(guid) ?? {}), ...patch });
  }
  ops(): ModelEditOp[] {
    return [...this.sets].map(([guid, set]) => ({ op: 'wall.update', guid, set }));
  }
}

/**
 * Endpoint `end` of wall `guid` dragged to `to`. With `keepCorners` the
 * walls joined at that end are dragged along (their shared end moves to
 * `to` as well), so a corner stays a corner (Revit; Ctrl lets go).
 */
export function dragEndpointOps(model: GeometriModel, guid: string, end: WallEnd, to: XY, keepCorners = true): ModelEditOp[] {
  const w = wallsOf(model).find((x) => x.guid === guid);
  if (!w || dist(w[end], to) < 1e-6) return [];
  const other = end === 'start' ? w.end : w.start;
  if (dist(other, to) < 0.01) return [];
  const batch = new Batch();
  batch.set(guid, { [end]: pt(to) });
  if (keepCorners) for (const j of joinedAt(wallsOf(model), w, end)) batch.set(j.wall.guid, { [j.end]: pt(to) });
  return batch.ops();
}

/**
 * Wall `guid` moved `offset` metres along its normal (its direction
 * kept). Every wall that ends on it slides its end along its own line to
 * the moved axis (corners and tees stay closed); a wall parallel to it
 * cannot meet it and is left as it is.
 */
export function moveWallOps(model: GeometriModel, guid: string, offset: number): ModelEditOp[] {
  const w = wallsOf(model).find((x) => x.guid === guid);
  const f = w ? frame(w) : null;
  if (!w || !f || Math.abs(offset) < 1e-6) return [];
  const shift = mul(f.normal, offset);
  const start = add(w.start, shift);
  const end = add(w.end, shift);
  const batch = new Batch();
  batch.set(guid, { start: pt(start), end: pt(end) });
  for (const t of endingOn(wallsOf(model), w)) {
    const o = t.wall;
    const od = sub(o.end, o.start);
    const x = lineIntersection(o.start, od, start, f.dir);
    if (!x) continue;
    batch.set(o.guid, { [t.end]: pt(x) });
  }
  return batch.ops();
}

/** The thickness the face grip gives with the pointer at `p`: twice its distance from the axis line. */
export function thicknessAt(w: SolidWall, p: XY): number | null {
  const f = frame(w);
  if (!f) return null;
  return Math.abs(dot(sub(p, w.start), f.normal)) * 2;
}

export function thicknessOps(w: SolidWall, thickness: number): ModelEditOp[] {
  if (!(thickness > 0.005) || (w.thickness !== null && Math.abs(thickness - w.thickness) < 5e-4)) return [];
  return [{ op: 'wall.update', guid: w.guid, set: { thickness: r6(thickness), assumed: false } }];
}

/** The signed offset of `p` from the wall's axis line, along its normal. */
export function offsetAt(w: SolidWall, p: XY): number | null {
  const f = frame(w);
  return f ? dot(sub(p, w.start), f.normal) : null;
}

// ------------------------------------------------- typed input (SketchUp) --

/**
 * An endpoint placed by typed values, from the fixed end `from`: `length`
 * metres along `dir` (the drag's direction), or at `angleDeg` (degrees from
 * world +x, counter-clockwise) when one was typed.
 */
export function typedEndpoint(from: XY, dir: XY, length: number, angleDeg: number | null = null): XY | null {
  if (!(length > 0)) return null;
  let u = dir;
  if (angleDeg !== null && Number.isFinite(angleDeg)) {
    const a = (angleDeg * Math.PI) / 180;
    u = [Math.cos(a), Math.sin(a)];
  }
  const L = Math.hypot(u[0], u[1]);
  if (!(L > 1e-9)) return null;
  return add(from, mul(u, length / L));
}

/** The drag's angle for the readout, degrees from world +x in [0, 360). */
export function angleOf(from: XY, to: XY): number {
  const a = (Math.atan2(to[1] - from[1], to[0] - from[0]) * 180) / Math.PI;
  return (a + 360) % 360;
}

// ---------------------------------------------- temporary dimensions --

export interface TempDim {
  id: 'length' | 'left' | 'right';
  /** Witness line ends (world), the dimension drawn between them. */
  from: XY;
  to: XY;
  /** Metres. */
  value: number;
  /** Where the label sits. */
  at: XY;
  /** For `left` / `right`: the neighbour it measures to. */
  neighbour?: string;
}

/** Walls closer to parallel than this (radians, ~3°) count as parallel neighbours. */
const PARALLEL = (3 * Math.PI) / 180;

/**
 * Revit's temporary dimensions for a selected wall: its length (along it,
 * on its left), and axis-to-axis distances to the nearest parallel wall on
 * each side whose span overlaps it, measured through the middle of the
 * overlap.
 */
export function tempDims(model: GeometriModel, guid: string): TempDim[] {
  const walls = wallsOf(model);
  const w = walls.find((x) => x.guid === guid);
  const f = w ? frame(w) : null;
  if (!w || !f) return [];
  const half = (w.thickness ?? 0) / 2;
  const out: TempDim[] = [];
  const lift = mul(f.normal, half + 0.35);
  out.push({ id: 'length', from: add(w.start, lift), to: add(w.end, lift), value: f.length, at: add(mul(add(w.start, w.end), 0.5), lift) });

  let left: { o: SolidWall; d: number; s: number } | null = null;
  let right: { o: SolidWall; d: number; s: number } | null = null;
  for (const o of walls) {
    if (o.guid === w.guid || o.storey_id !== w.storey_id) continue;
    const of = frame(o);
    if (!of || Math.abs(Math.abs(dot(of.dir, f.dir)) - 1) > 1 - Math.cos(PARALLEL)) continue;
    const t0 = dot(sub(o.start, w.start), f.dir);
    const t1 = dot(sub(o.end, w.start), f.dir);
    const lo = Math.max(0, Math.min(t0, t1));
    const hi = Math.min(f.length, Math.max(t0, t1));
    if (hi - lo < 0.05) continue;
    const d = dot(sub(o.start, w.start), f.normal);
    if (Math.abs(d) < 0.01) continue;
    const s = (lo + hi) / 2;
    if (d > 0 && (!left || d < left.d)) left = { o, d, s };
    if (d < 0 && (!right || -d < right.d)) right = { o, d: -d, s };
  }
  for (const [id, hit, sign] of [
    ['left', left, 1],
    ['right', right, -1],
  ] as const) {
    if (!hit) continue;
    const base = add(w.start, mul(f.dir, hit.s));
    const to = add(base, mul(f.normal, sign * hit.d));
    out.push({ id, from: base, to, value: hit.d, at: mul(add(base, to), 0.5), neighbour: hit.o.guid });
  }
  return out;
}

/**
 * A temporary dimension typed to `value`: the length moves the wall's end
 * (the start stays, corners at the end follow); a side distance moves the
 * wall along its normal so it stands `value` from that neighbour.
 */
export function tempDimOps(model: GeometriModel, guid: string, dim: TempDim, value: number, keepCorners = true): ModelEditOp[] {
  if (!(value > 0) || Math.abs(value - dim.value) < 5e-4) return [];
  const w = wallsOf(model).find((x) => x.guid === guid);
  const f = w ? frame(w) : null;
  if (!w || !f) return [];
  if (dim.id === 'length') return dragEndpointOps(model, guid, 'end', add(w.start, mul(f.dir, value)), keepCorners);
  const sign = dim.id === 'left' ? 1 : -1;
  return moveWallOps(model, guid, sign * (dim.value - value));
}
